/**
 * AgentRegistry -- FUENTE UNICA de identidad de los agentes del ecosistema Nexo.
 *
 * PANEL-AGENTS-DASHBOARD-V1-001 (B: drift kill). Antes la identidad vivia
 * fragmentada en 3 listas hardcodeadas que podian driftear:
 *   - KNOWN_AGENTS   (AgentsDashboardApiClient) — codigos para el fallback
 *   - EXECUTOR_AGENTS(AgentsDashboardView)      — subset ejecutores (review queue)
 *   - AGENT_PROFILE  (AgentsDashboardView)      — avatar/alias/color/short
 * Ahora las 3 se DERIVAN de este registro. El canonical en runtime sigue siendo
 * nexo_private.nexo_agents (via endpoint /agents/dashboard); este registro es el
 * fallback + metadata de presentacion, en UN solo lugar.
 *
 * Convencion de identidad: codigo canonico UPPERCASE. bitacora.origen viene
 * lowercase (legacy) -> normalizar con normalizeAgentCode() antes de comparar.
 *
 * @module infrastructure/api/AgentRegistry
 */

// role: 'orchestrator' (Ares) | 'principal' (Argemiro y su cliente) | 'executor' (Argys que ejecutan misiones) | 'legacy'
export const AGENT_REGISTRY = [
  { code: 'ARES-001',               displayName: 'Ares',         role: 'orchestrator', avatar: '🧠',   alias: 'El Sombrero Seleccionador / La Nave de Rick',   color: '#bc8cff' },
  { code: 'ARGEMIRO-001',           displayName: 'Argemiro',     role: 'principal',    avatar: '🧙',   alias: 'Patronus Mayor / Council of Ricks',             color: '#d29922' },
  { code: 'ARGEMIRO-CLIENTE-001',   displayName: 'Argy X',       role: 'principal',    avatar: '📞',   alias: 'Patronus Mensajero / Operating Phone',          color: '#79c0ff' },
  { code: 'ARGY-PANEL-LOCAL-001',   displayName: 'Argy Panel',   role: 'executor',     avatar: '🦝',   alias: 'Diseñador del Mapa del Merodeador / Squanchy',  color: '#58a6ff' },
  { code: 'ARGY-API-LOCAL-001',     displayName: 'Argy API',     role: 'executor',     avatar: '🦅',   alias: 'Constructor del Salon de Pociones / Bird Person', color: '#a371f7' },
  { code: 'ARGY-DESKTOP-LOCAL-001', displayName: 'Argy Desktop', role: 'executor',     avatar: '👨‍🔧', alias: 'Forjador del Caldero / Mr. Poopybutthole',      color: '#3fb950' },
  { code: 'ARGY-Q-LOCAL-001',       displayName: 'Argy Q',       role: 'executor',     avatar: '🔍',   alias: 'Inquisidor del Dpto. de Misterios / Glootie',   color: '#f78166' },
  { code: 'ARGY-W-LOCAL-001',       displayName: 'Argy W',       role: 'executor',     avatar: '🌐',   alias: 'Newt Scamander del Mundo Magico / Mr. Meeseeks',  color: '#56d4dd' },
  { code: 'ARGYS-001',              displayName: 'Argys',        role: 'legacy',       avatar: '🛠',   alias: 'Eventual Cursor (legacy desktop V1)',           color: '#6e7681' }
];

// Indice code -> entry (para lookup O(1)).
const _byCode = new Map(AGENT_REGISTRY.map(a => [a.code, a]));

// Codigos canonicos (reemplaza KNOWN_AGENTS hardcodeado).
export const AGENT_CODES = AGENT_REGISTRY.map(a => a.code);

// Codigos ejecutores (reemplaza EXECUTOR_AGENTS hardcodeado).
export const EXECUTOR_CODES = AGENT_REGISTRY.filter(a => a.role === 'executor').map(a => a.code);

// PANEL-AGENTS-DS-REDESIGN-V1-001 ADS-T5: agentes legacy/retirados. ARGYS-001 (role legacy en
// el registro) + codigos historicos previos a los territorios que ya no estan registrados pero
// aun tienen transacciones NXS en el banco (no se borran, se agrupan aparte).
export const LEGACY_CODES = new Set([
  ...AGENT_REGISTRY.filter(a => a.role === 'legacy').map(a => a.code),
  'ARGYS-PANEL-001', 'ARGYS-API-001'
]);

/** true si el codigo es un agente legacy/retirado (excluir de vistas live, agrupar en el banco). */
export function isLegacyAgent(code) {
  return LEGACY_CODES.has(normalizeAgentCode(code));
}

/**
 * Normaliza cualquier variante de identidad (bitacora.origen lowercase, users
 * UPPERCASE, etc.) al codigo canonico. Garantiza que 'ares-001' y 'ARES-001'
 * resuelvan al MISMO agente (A: alineacion de case).
 * @param {string} raw
 * @returns {string|null}
 */
export function normalizeAgentCode(raw) {
  if (!raw) return null;
  return String(raw).trim().toUpperCase();
}

/** Metadata canonica de un agente (o null si no esta registrado). */
export function agentProfile(code) {
  return _byCode.get(normalizeAgentCode(code)) || null;
}

/** displayName canonico; fallback al codigo si el agente no esta registrado. */
export function displayNameFor(code) {
  const c = normalizeAgentCode(code);
  return (_byCode.get(c)?.displayName) || c || '';
}

/** true si el codigo pertenece a un agente ejecutor de misiones. */
export function isExecutor(code) {
  return _byCode.get(normalizeAgentCode(code))?.role === 'executor';
}

// ---------------------------------------------------------------------------
// PERTENENCIA vs DECORACION (hallazgo de Argy-Q #10940 sobre El Gremio).
//
// Este registro NO decide QUIENES hay: eso lo dice nexo_private.nexo_agents,
// via GET /api/v1/agents/dashboard. Aqui vive solo como se los VE -- avatar,
// color y alias -- que no salen de ninguna tabla y por eso siguen a mano.
//
// El fallo que esto cierra no se nota: si una pantalla recorre esta lista, un
// agente que no este en ella no aparece gris ni dormido, no aparece; y un
// cuarto al que le falta un personaje se ve igual de lleno. MEDIDO el 4-sep:
// nexo_agents tiene 11 y esta lista 9. Los dos sin puesto eran
// CHATGPT-JOSSE-001 (el tutor de ingles) y MEMORY-COMPILER-N8N-001.
// ---------------------------------------------------------------------------

/**
 * Origenes que ESCRIBEN en la bitacora y NO son agentes del equipo: procesos.
 *
 * POR QUE LISTA NEGRA Y NO LA BLANCA DE AGENT_CODES: con la negra un agente
 * nuevo cuenta desde su primera anotacion y lo unico que hay que mantener es la
 * lista de robots; con la blanca hay que acordarse de agregarlo, y olvidarlo no
 * da error, da un numero mas bajo.
 *
 * OJO, y por eso se anota: la justificacion original (#267) citaba a
 * ARGY-W-LOCAL-001 como el agente que la lista blanca dejaria fuera. Dejo de
 * ser cierto el MISMO DIA, en el PR siguiente (#268), que lo metio al registro,
 * y el comentario se quedo repitiendolo. Medido de nuevo el 4-sep contra la BD:
 * los unicos que escriben en 7 dias sin estar en nexo_agents son SYNC-UPLOAD
 * (863 anotaciones) y NEXO-API (26). El argumento sigue en pie; el ejemplo no.
 */
export const ORIGENES_NO_AGENTE = ['SYNC-UPLOAD', 'NEXO-API', 'NX-PANEL'];

/** true si el origen es un proceso y no una persona ni un agente. */
export function esOrigenDeProceso(code) {
  return ORIGENES_NO_AGENTE.includes(normalizeAgentCode(code));
}

/**
 * Perfil para pintar a CUALQUIER agente, este o no en el registro.
 *
 * Un codigo desconocido recibe un muneco generico con su nombre -- el de la BD
 * si la API lo trajo, si no el codigo -- en vez de no recibir ninguno. Asi la
 * sala no puede verse completa mientras falta alguien.
 *
 * @param {string} code
 * @param {string} [nombreDeLaApi] displayName que trajo la API (el name de nexo_agents)
 * @returns {{code:string, displayName:string, avatar:string, alias:string, color:string, role:string}}
 */
export function perfilOGenerico(code, nombreDeLaApi) {
  const c = normalizeAgentCode(code);
  const ficha = _byCode.get(c);
  if (ficha) return ficha;
  return {
    code: c,
    displayName: nombreDeLaApi || c,
    role: 'sin-ficha',
    avatar: '\u{1F464}',
    alias: 'Sin ficha en el registro del panel',
    color: '#8b949e'
  };
}
